import {
    useEffect,
    useMemo,
    useState
} from "react";
import { useSession } from "next-auth/react";
import {
    useParams,
    usePathname,
    useSelectedLayoutSegment
} from "next/navigation";
import {
    ConfigProvider,
    Menu
} from "antd";
import {
    useLocale,
    useTranslations
} from "next-intl";
import cn from "classnames";
import s from '@/theme/ui/sidebar/menu.module.scss'
import { menuItems } from "@/theme/mainLayout/sidebar/menuItems";
import {
    isShowLinkInNavBar,
    myLinks
} from "@/lib/routes/myLinks";
import { useAuth } from "@/lib/hook/useAuth";
import { parseRoute } from "@/lib/routes/accessRoute";
import Link from "next/link";
import {
    CategoriesIcon,
    ConnectionsIcon,
    CustomersIcon,
    DashboardIcon,
    MarketingIcon,
    PurchasesIcon, ReportsIcon,
    RolesIcon,
    SupportIcon,
    SystemEditionIcon,
    UserRoles, WalletMenuIcon
} from "@/theme/assets/icons";
import { locales } from '@/navigation';
export const NavMenu = (
    {
        collapsed,
        setCollapsed
    }
) => {
    const t = useTranslations('sidebar');
    const { data: session } = useSession();
    const auth = useAuth();
    const params = useParams();
    const pathname = usePathname();
    const locale = useLocale();
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [openKeys, setOpenKeys] = useState([]);
    const activeSegment = useSelectedLayoutSegment();

    useEffect(()=>{
        if (auth.user.id){
            const link = getRoute(pathname, params)

            if (link){
                const linkKey = link.parent_key ? link.parent_key : link.key
                const keys = getSelectedKeysByKey(mainMenu, linkKey)

                setSelectedKeys(()=>[...keys])
                setOpenKeys(()=>keys.slice(0,keys.length-1))
            }else {
                setSelectedKeys([])
                setOpenKeys([])
            }
        }
    }, [pathname, auth.user])

    const mainMenu = useMemo(()=>{
        const parseMenu = (menu) =>{
            return menu.filter((item)=>{
                if (item.children){
                    item.children = parseMenu(item.children)
                }

                return isShowLinkInNavBar(item, auth.user)
            })
        }

        if (auth.user.id){
            return parseMenu(menuItems)
        }

        return []
    }, [auth.user]);

    const getRoute = (pathname, params) =>{
        const url = parseRoute(params, pathname)

        return myLinks.getByUrl(url)
    }

    const getSelectedKeysByKey = (mainMenu, keyFind) =>{
        const getKeys = (menu, keys = []) => {
            menu.forEach(({key, children, type}) => {
                if (key === keyFind && type !== 'group') {
                    keys.push(key);
                }
                if (children && !keys.length) {
                    const k = getKeys(children, keys);

                    if (k.length && type !== 'group'){
                        keys.push(key);
                    }
                }
            });

            return keys;
        }

        return getKeys(mainMenu).reverse()
    }

    const getLink = (url) => {
        if (locales.includes(locale) && locale !== 'en'){
            url = '/' + locale + url
        }

        return url;
    }

    const generalGroup = [
        {
            'title': t('dashboard'),
            'link': '/',
            'icon': <DashboardIcon />,
            'key': 'dashboard',
            'permissions': ['dashboard-view']
        },
        {
            'title': t('customers'),
            'link': '/customers',
            'icon': <CustomersIcon />,
            'key': 'customers',
            'permissions': ['customers-view']
        },
        {
            'title': t('purchases'),
            'link': '/purchases',
            'icon': <PurchasesIcon />,
            'key': 'purchases',
            'permissions': ['purchases-view']
        },
        {
            'title': t('connections'),
            'link': '/connections',
            'icon': <ConnectionsIcon />,
            'key': 'connections',
            'permissions': ['connections-view']
        },
        {
            'title': t('support'),
            'link': '/support',
            'icon': <SupportIcon />,
            'key': 'support',
            'permissions': ['support-view']
        },
        {
            'title': t('reports'),
            'link': '/reports',
            'icon': <ReportsIcon />,
            'key': 'reports',
            'permissions': ['reports-view']
        },
        {
            'title': t('wallet'),
            'link': '/wallet',
            'icon': <WalletMenuIcon />,
            'key': 'wallet',
            'permissions': []
        },
    ]

    const settingsGroup = [
        {
            'title': t('categories'),
            'link': '/categories',
            'icon': <CategoriesIcon />,
            'key': 'categories',
            'type': 'main',
            'permissions': ['categories-view']
        },
        {
            'title': t('landing'),
            'link': '/system/landing',
            'icon': <SystemEditionIcon />,
            'key': 'system',
            'type': 'sub',
            'permissions': ['system-view']
        },
        {
            'title': t('marketing'),
            'link': '/marketing',
            'icon': <MarketingIcon />,
            'key': 'marketing',
            'type': 'main',
            'permissions': ['marketing-view']
        },
        {
            'title': t('users'),
            'link': '/users',
            'icon': <UserRoles />,
            'key': 'users',
            'type': 'main',
            'permissions': ['users-view']
        },
        {
            'title': t('roles'),
            'link': '/roles',
            'icon': <RolesIcon />,
            'key': 'roles',
            'type': 'main',
            'permissions': ['manage-roles']
        },
    ]

    return (
        <>
            <div className={cn(s.menu, 'ant_menu_wrapper')}>
                <ConfigProvider>
                    <Menu
                        mode="inline"
                        selectedKeys={selectedKeys}
                        inlineIndent={15}
                        onClick={() => {
                            if (setCollapsed) {
                                setCollapsed()
                            }
                        }}
                    >
                        <Menu.ItemGroup title={t('general')}>
                            {generalGroup.map((item, i) => {
                                const hasPermission =
                                    item.permissions.length === 0 ||
                                    item.permissions?.some(p => session?.user?.permissions?.includes(p));

                                if (!hasPermission) return null;

                                return (
                                    <Menu.Item
                                        key={item.key}
                                        className={cn({
                                            'ant-menu-item-selected': item.key === activeSegment || activeSegment === null && item.key === 'dashboard',
                                            'change_font': locale === 'ru' || locale === 'ua' || locale === 'fr' || locale === 'es' || locale === 'it' || locale === 'pl',
                                        })}
                                    >
                                        <Link href={getLink(item.link)}>
                                            {item.icon}
                                            <span className="nav-text">{item.title}</span>
                                        </Link>
                                    </Menu.Item>
                                )
                            })}
                        </Menu.ItemGroup>
                        <Menu.ItemGroup title={t('settings')}>
                            {settingsGroup.map((item, i) => {
                                const hasPermission =
                                    item.permissions.length === 0 ||
                                    item.permissions?.some(p => session?.user?.permissions?.includes(p));

                                if (!hasPermission) return null;

                                if (item.type === 'main') {
                                    return (
                                        <Menu.Item
                                            key={item.key}
                                            className={cn({
                                                'ant-menu-item-selected': item.key === activeSegment || activeSegment === null && item.key === 'dashboard',
                                                'change_font': locale === 'ru' || locale === 'ua' || locale === 'fr' || locale === 'es' || locale === 'it' || locale === 'pl',
                                            })}
                                        >
                                            <Link href={getLink(item.link)}>
                                                {item.icon}
                                                <span className="nav-text">{item.title}</span>
                                            </Link>
                                        </Menu.Item>
                                    );
                                } else {
                                    return (
                                        <Menu.SubMenu
                                            className={locale === 'ru' || locale === 'ua' || locale === 'fr' || locale === 'es' || locale === 'it' || locale === 'pl' ? 'change_font_sub' : ''}
                                            key='sub' title={t('system')} icon={item.icon}>
                                            <Menu.Item
                                                key={item.key}
                                                className={cn({
                                                    'ant-menu-item-selected': item.key === activeSegment || activeSegment === null && item.key === 'dashboard'
                                                })}
                                            >
                                                <Link href={getLink(item.link)}>
                                                    <span className="nav-text">{item.title}</span>
                                                </Link>
                                            </Menu.Item>
                                        </Menu.SubMenu>
                                    );
                                }
                            })}
                        </Menu.ItemGroup>
                    </Menu>
                </ConfigProvider>
            </div>
        </>
    );
}